import { Button, Icon, SelectMenu, Typography } from '@scale/scaleui';
import { useEffect, useMemo, useState } from 'react';
import { AUDIT_LEVELS_TO_ADD } from '../../../audits/SpeedAudit/v2/constants';

export type TaskFilters = { reviewLevels?: number[]; hasBeenAudited?: boolean };

const InitialTaskFilterOptions = [
  {
    id: 'All Attempts',
    name: 'All Attempts',
  },
  {
    id: 'All Unaudited Attempts',
    name: 'All Unaudited Attempts',
  },
  {
    id: 'Custom Filter',
    name: 'Custom Filter',
  },
];

const DEFAULT_FILTER_OPTION = InitialTaskFilterOptions[0].id;

interface FilterOption {
  id: string;
  name: string;
}
interface FilterObject {
  primary: string;
  secondary?: string;
  tertiary?: string | string[];
  secondaryOptions?: FilterOption[];
  tertiaryOptions?: FilterOption[];
}

const CustomFilterOptions = [
  {
    id: 'Review Level',
    name: 'Review Level',
    secondaryOptions: [{ id: 'is', name: 'is' }],
    tertiaryOptions: [
      { id: '-1', name: 'Attempt' },
      { id: '0', name: 'L0' },
      { id: '1', name: 'L1' },
      { id: '4', name: 'L4' },
      { id: '10', name: 'L10' },
      { id: '12', name: 'L12' },
    ],
  },
  {
    id: 'Has been Audited',
    name: 'Has been Audited',
    secondaryOptions: [{ id: 'is', name: 'is' }],
    tertiaryOptions: [
      { id: 'true', name: 'True' },
      { id: 'false', name: 'False' },
    ],
  },
];
const DEFAULT_CUSTOM_FILTER_OPTION = CustomFilterOptions[0].id;

export const useChatBulkAuditFilterComponents = (projectReviewLevels: number[]) => {
  const [selectedQuickFilter, setSelectedQuickFilter] = useState(DEFAULT_FILTER_OPTION);

  const quickFilterOptions = useMemo(() => {
    return [
      ...InitialTaskFilterOptions,
      ...AUDIT_LEVELS_TO_ADD.filter(({ level }) => projectReviewLevels.includes(level)).map(
        ({ filter }) => ({
          id: filter,
          name: filter,
        }),
      ),
    ];
  }, [projectReviewLevels]);

  const customFilterOptions = useMemo(() => {
    return CustomFilterOptions.map(filter => {
      if (filter.id === 'Review Level') {
        const updatedTertiaryOptions = projectReviewLevels.map((level: number) => ({
          id: `${level}`,
          name: `L${level}`,
        }));

        return {
          ...filter,
          tertiaryOptions: updatedTertiaryOptions,
        };
      }
      return filter;
    });
  }, [projectReviewLevels]);

  const [customFilters, setCustomFilters] = useState<FilterObject[]>([
    {
      primary: DEFAULT_CUSTOM_FILTER_OPTION,
      secondary: undefined,
      tertiary: undefined,
      secondaryOptions: customFilterOptions[0].secondaryOptions || [],
      tertiaryOptions: customFilterOptions[0].tertiaryOptions || [],
    },
  ]);

  // update default filters and review level selections based on project's review levels
  useEffect(() => {
    setCustomFilters(currentFilters =>
      currentFilters.map(filter => {
        if (filter.primary === 'Review Level') {
          const updatedTertiaryOptions = projectReviewLevels.map((level: number) => ({
            id: `${level}`,
            name: `L${level}`,
          }));

          return {
            ...filter,
            tertiaryOptions: updatedTertiaryOptions,
          };
        }
        return filter;
      }),
    );
  }, [projectReviewLevels]);

  const bodyFilters = useMemo(() => {
    const bodyFilters: TaskFilters = {};
    if (selectedQuickFilter !== 'All Attempts') {
      if (selectedQuickFilter === 'Custom Filter') {
        customFilters.forEach(filter => {
          if (filter.primary === 'Review Level' && filter.secondary && filter.tertiary) {
            bodyFilters.reviewLevels = bodyFilters.reviewLevels || [];
            if (Array.isArray(filter.tertiary)) {
              filter.tertiary.forEach(tertiaryItem => {
                const parsedValue = parseInt(tertiaryItem, 10);
                if (!isNaN(parsedValue) && bodyFilters.reviewLevels) {
                  bodyFilters.reviewLevels.push(parsedValue);
                }
              });
            } else {
              const parsedValue = parseInt(filter.tertiary, 10);
              if (!isNaN(parsedValue)) {
                bodyFilters.reviewLevels.push(parsedValue);
              }
            }
          } else if (filter.primary === 'Has been Audited') {
            bodyFilters.hasBeenAudited = filter.tertiary === 'true';
          }
        });
      } else if (selectedQuickFilter === 'All Unaudited Attempts') {
        bodyFilters.hasBeenAudited = false;
      } else {
        const level = AUDIT_LEVELS_TO_ADD.find(
          ({ filter }) => filter === selectedQuickFilter,
        )?.level;
        if (level !== undefined) {
          bodyFilters.reviewLevels = [level];
        }
      }
    }

    return bodyFilters;
  }, [selectedQuickFilter, customFilters]);

  const addFilter = () => {
    setCustomFilters(currentFilters => {
      const primaryOption = customFilterOptions?.find(
        option => option.id === DEFAULT_CUSTOM_FILTER_OPTION,
      );
      const newFilters = [
        ...currentFilters,
        {
          primary: DEFAULT_CUSTOM_FILTER_OPTION,
          secondary: undefined,
          tertiary: undefined,
          secondaryOptions: primaryOption?.secondaryOptions || [],
          tertiaryOptions: primaryOption?.tertiaryOptions || [],
        },
      ];
      return newFilters;
    });
  };

  const removeFilter = (index: number) => {
    setCustomFilters(currentFilters => currentFilters.filter((_, i) => i !== index));
  };

  const selectCustomFilterChange = (
    selectedId: string | string[],
    index: number,
    filterType: string,
  ) => {
    setCustomFilters(currentFilters =>
      currentFilters.map((filter, i) => {
        if (i === index) {
          switch (filterType) {
            case 'primary': {
              const primarySelectedId = Array.isArray(selectedId) ? selectedId[0] : selectedId; // primary will always have a single value
              const primaryOption = customFilterOptions?.find(
                option => option.id === primarySelectedId,
              );
              return {
                ...filter,
                primary: primarySelectedId,
                secondary: undefined,
                tertiary: undefined,
                secondaryOptions: primaryOption?.secondaryOptions || [],
                tertiaryOptions: primaryOption?.tertiaryOptions || [],
              };
            }
            case 'secondary': {
              const secondarySelectedId = Array.isArray(selectedId) ? selectedId[0] : selectedId; // secondary will always have a single value
              return { ...filter, secondary: secondarySelectedId };
            }
            case 'tertiary':
              return { ...filter, tertiary: selectedId };
            default:
              return filter;
          }
        }
        return filter;
      }),
    );
  };

  const renderQuickFiltersComponent = () => (
    <>
      <div className="flex-1 space-y-2">
        <Typography.Subtitle3>Filter Selection</Typography.Subtitle3>
        <Typography.Caption>
          Select a quick filter from the dropdown or use custom logic
        </Typography.Caption>
      </div>
      <SelectMenu
        options={quickFilterOptions}
        selectedOptionId={selectedQuickFilter}
        setSelected={selectedId => setSelectedQuickFilter(selectedId)}
      />
    </>
  );

  const renderCustomFiltersComponent = () => (
    <>
      {customFilters.map((filter, index) => (
        <div key={index} className="ml-8">
          <div className="flex flex-row items-center space-x-4">
            <SelectMenu
              className="w-48"
              options={customFilterOptions}
              selectedOptionId={filter.primary}
              setSelected={selectedId => selectCustomFilterChange(selectedId, index, 'primary')}
            />
            <SelectMenu
              className="w-48"
              options={filter.secondaryOptions || []}
              selectedOptionId={filter.secondary || ''}
              setSelected={selectedId => selectCustomFilterChange(selectedId, index, 'secondary')}
            />
            {filter.primary === 'Review Level' && (
              <SelectMenu
                className="w-48"
                multiple
                options={filter.tertiaryOptions || []}
                selectedOptionId={(filter.tertiary as string[]) || []}
                setSelected={selectedId =>
                  selectCustomFilterChange(selectedId as string[], index, 'tertiary')
                }
              />
            )}
            {filter.primary !== 'Review Level' && (
              <SelectMenu
                className="w-48"
                options={filter.tertiaryOptions || []}
                selectedOptionId={(filter.tertiary as string) || ''}
                setSelected={selectedId => selectCustomFilterChange(selectedId, index, 'tertiary')}
              />
            )}
            <Icon
              className=""
              icon={['fas', 'window-minimize']}
              onClick={() => removeFilter(index)}
              size="sm"
              tooltip=""
              variant="neutral"
            />
          </div>
        </div>
      ))}
      <Button className="ml-8" variant="primary" onClick={addFilter}>
        Add Filter
      </Button>
    </>
  );
  return {
    bodyFilters,
    renderCustomFiltersComponent,
    renderQuickFiltersComponent,
    selectedQuickFilter,
  };
};
